import React, { useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList,
} from "recharts";
import { BarChart3 } from "lucide-react";
import { COLORS } from "@/lib/api";

const EJE_COLORS = {
  "Calles y Vialidad": "#C17A5A",
  "Drenaje y Pluviales": "#A5613F",
  "Espacios Públicos y Áreas Verdes": "#16A34A",
  "Obras Públicas e Infraestructura": "#7C3AED",
  "Obra Pública e Infraestructura": "#7C3AED",
  "Bienestar Social y Apoyos": "#DB2777",
  "Salud": "#DC2626",
  "Deporte y Juventud": "#EA580C",
  "Cultura y Educación": "#CA8A04",
  "Ecología y Protección Animal": "#059669",
};

const RAC_PALETTE = [
  "#C03C54", "#A52F43", "#E06377", "#7A1C2C", "#D97706",
  "#C17A5A", "#16A34A", "#7C3AED", "#DB2777", "#5A524A",
];

function shorten(name, n = 26) {
  return name.length > n ? name.slice(0, n - 1) + "…" : name;
}

export default function ClassificationChart({ summary }) {
  const [tab, setTab] = useState("actividades");
  const data =
    tab === "actividades" ? summary?.cat_actividades || []
    : tab === "activaciones" ? summary?.cat_activaciones || []
    : summary?.cat_rac || [];
  const colorFor = (name, i) =>
    tab === "actividades" ? EJE_COLORS[name] || COLORS.actividades
    : tab === "activaciones" ? COLORS.activaciones
    : RAC_PALETTE[i % RAC_PALETTE.length];

  const chartData = data.map((d) => ({ ...d, short: shorten(d.name) }));
  const height = Math.max(180, chartData.length * 34);

  const subtitle =
    tab === "actividades" ? "Obras por Eje (9 Ejes)"
    : tab === "activaciones" ? "Activaciones por Tipo de Evento"
    : "Reportes por Asunto";

  return (
    <div data-testid="classification-chart"
      className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-guinda-light text-guinda flex items-center justify-center">
            <BarChart3 className="w-4 h-4" />
          </span>
          <div>
            <h3 className="font-heading text-base font-bold text-slate-900 leading-tight">
              Realizado por Clasificación
            </h3>
            <p className="text-[11px] text-slate-400">
              {subtitle}
            </p>
          </div>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
          <button data-testid="chart-tab-actividades" onClick={() => setTab("actividades")}
            className={`text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${
              tab === "actividades" ? "bg-white text-terracota shadow-sm" : "text-slate-500"
            }`}>
            Actividades
          </button>
          <button data-testid="chart-tab-rac" onClick={() => setTab("rac")}
            className={`text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${
              tab === "rac" ? "bg-white text-guinda shadow-sm" : "text-slate-500"
            }`}>
            Reportes
          </button>
          <button data-testid="chart-tab-activaciones" onClick={() => setTab("activaciones")}
            className={`text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${
              tab === "activaciones" ? "bg-white shadow-sm" : "text-slate-500"
            }`}
            style={tab === "activaciones" ? { color: "#5A524A" } : undefined}>
            Activaciones
          </button>
        </div>
      </div>

      {chartData.length === 0 ? (
        <p className="text-sm text-slate-400 py-10 text-center">
          {summary ? "Sin registros para esta clasificación." : "Cargando..."}
        </p>
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={chartData} layout="vertical"
            margin={{ top: 4, right: 40, left: 8, bottom: 4 }}>
            <XAxis type="number" hide />
            <YAxis type="category" dataKey="short" width={190}
              tick={{ fontSize: 11, fill: "#475569" }} axisLine={false} tickLine={false} />
            <Tooltip
              formatter={(v) => [`${v} registros`, ""]}
              labelFormatter={(l, p) => (p && p[0] ? p[0].payload.name : l)}
              contentStyle={{ fontSize: 12, borderRadius: 10, border: "1px solid #e2e8f0" }}
              cursor={{ fill: "rgba(192,60,84,0.06)" }} />
            <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={20} isAnimationActive>
              {chartData.map((entry, i) => (
                <Cell key={entry.name} fill={colorFor(entry.name, i)} />
              ))}
              <LabelList dataKey="value" position="right"
                style={{ fontSize: 11, fontWeight: 700, fill: "#334155" }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
